import React, { Component, ReactNode } from "react"
import { Box, Typography, Button, Container } from "@mui/material"
import { ErrorOutline } from "@mui/icons-material"

interface Props {
	children: ReactNode
}

interface State {
	hasError: boolean
	error: Error | null
	errorInfo: React.ErrorInfo | null
}

/**
 * Error Boundary для глобальной обработки ошибок React
 *
 * Использование:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
		}
	}

	static getDerivedStateFromError(error: Error): Partial<State> {
		return { hasError: true, error }
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// В development режиме логируем в консоль
		if (process.env.NODE_ENV === 'development') {
			// eslint-disable-next-line no-console
			console.error("ErrorBoundary caught an error:", error, errorInfo)
		}

		// TODO: Отправить ошибку в Sentry, LogRocket или другой сервис мониторинга
		// Sentry.captureException(error, { contexts: { react: errorInfo } })

		this.setState({ errorInfo })
	}

	handleReload = () => {
		window.location.reload()
	}

	handleGoBack = () => {
		window.history.back()
	}

	handleGoHome = () => {
		window.location.href = "/"
	}

	render() {
		if (this.state.hasError) {
			return (
				<Container maxWidth="sm">
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							minHeight: "100vh",
							textAlign: "center",
							gap: 3,
							py: 4,
						}}
					>
						<ErrorOutline
							sx={{
								fontSize: { xs: 60, sm: 80 },
								color: "error.main",
								animation: "pulse 2s infinite",
								"@keyframes pulse": {
									"0%": { opacity: 1 },
									"50%": { opacity: 0.5 },
									"100%": { opacity: 1 },
								},
							}}
						/>

						<Box>
							<Typography
								variant="h4"
								gutterBottom
								sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
							>
								Что-то пошло не так
							</Typography>
							<Typography
								variant="body1"
								color="text.secondary"
								paragraph
								sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
							>
								{this.state.error?.message || "Произошла непредвиденная ошибка"}
							</Typography>
						</Box>

						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: "column", sm: "row" },
								gap: 2,
								width: "100%",
								maxWidth: 400,
							}}
						>
							<Button
								variant="contained"
								onClick={this.handleReload}
								fullWidth
							>
								Перезагрузить
							</Button>
							<Button
								variant="outlined"
								onClick={this.handleGoHome}
								fullWidth
							>
								На главную
							</Button>
						</Box>

						{/* Показываем детали ошибки только в development */}
						{process.env.NODE_ENV === "development" && this.state.errorInfo && (
							<Box
								sx={{
									mt: 4,
									p: 2,
									bgcolor: "grey.100",
									borderRadius: 1,
									width: "100%",
									maxHeight: 300,
									overflow: "auto",
								}}
							>
								<Typography
									variant="caption"
									component="pre"
									sx={{
										fontSize: "0.75rem",
										textAlign: "left",
										whiteSpace: "pre-wrap",
										wordBreak: "break-word",
									}}
								>
									{this.state.error?.stack}
									{"\n\n"}
									{this.state.errorInfo.componentStack}
								</Typography>
							</Box>
						)}
					</Box>
				</Container>
			)
		}

		return this.props.children
	}
}



